import React, { useState } from 'react';
import { parseForm } from '../../utils/formParser';

interface FormEditorProps {
  value: string;
  onChange: (value: string) => void;
  onPreview?: () => void;
  showPreview?: boolean;
  onTogglePreview?: (show: boolean) => void;
}

const EXAMPLE_FORM = `### Daily check-in

- [ ] I worked out today
- [ ] I stayed within calories

@field mood type=range min=1 max=10 label="Mood"
@field weight type=number unit=kg optional
@goalfield water goal=8 unit=glasses label="Water intake"

@text notes rows=3 placeholder="Anything worth noting?"`;

const HELP_TEXT = `
**Form Syntax:**

**Headers:**
### Title
## Subtitle

**Checkboxes:**
- [ ] Task item

---
**Inline Format (simple):**

**Range Field:**
@field name type=range min=1 max=10 label="Label"

**Number Field:**
@field name type=number unit=kg optional

**Counter Field (with +/- buttons):**
@field name type=counter min=0 max=20 label="Label" unit=glasses

**Goal Field (with progress bar):**
@goalfield name goal=10000 unit=steps label="Label"

**Text Area:**
@text name rows=3 placeholder="Placeholder text"

---
**Block Format (advanced):**

@field
  id: calories
  kind: number
  label: Daily calories
  units: kcal

@goalfield
  id: steps
  goal: 10000
  unit: steps
  label: Daily steps

@computed
  id: calories_7d_avg
  label: 7-day average
  kind: rolling_avg
  source: calories
  window: last_n_days(7)

---
**Math (inline):**
$BMI = weight / height^2$

**Math (block):**
$$
E = mc^2
$$
`;

/**
 * edit form text
 */
export function FormTextEditor({ value, onChange, onPreview, showPreview, onTogglePreview }: FormEditorProps) {
  const [showHelp, setShowHelp] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);

  const handleChange = (newValue: string) => {
    onChange(newValue);

    // Try to parse and show errors
    try {
      parseForm(newValue);
      setParseError(null);
    } catch (error) {
      setParseError(error instanceof Error ? error.message : 'Parse error');
    }
  };

  const loadExample = () => {
    onChange(EXAMPLE_FORM);
    setParseError(null);
  };

  // Parse current value to show stats
  let stats = { elements: 0, checkboxes: 0, fields: 0 };
  try {
    const schema = parseForm(value);
    stats = {
      elements: schema.elements.length,
      checkboxes: schema.checkboxCount,
      fields: schema.fieldCount,
    };
  } catch {
    // Ignore parse errors for stats
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowHelp(!showHelp)}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            {showHelp ? 'Hide' : 'Show'} Syntax Help
          </button>
          <span className="text-gray-300">|</span>
          <button
            onClick={loadExample}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            Load Example
          </button>
          {onTogglePreview && showPreview !== undefined && (
            <>
              <span className="text-gray-300">|</span>
              <div className="flex border border-gray-300 rounded-lg overflow-hidden">
                <button
                  onClick={() => onTogglePreview(false)}
                  className={`px-3 py-1 text-sm ${!showPreview
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  Edit
                </button>
                <button
                  onClick={() => onTogglePreview(true)}
                  className={`px-3 py-1 text-sm border-l border-gray-300 ${showPreview
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  Preview
                </button>
              </div>
            </>
          )}
        </div>
        <div className="text-xs text-gray-500">
          {stats.elements} elements · {stats.checkboxes} checkboxes · {stats.fields} fields
        </div>
      </div>

      {/* Help Panel */}
      {showHelp && (
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <pre className="text-xs text-gray-700 whitespace-pre-wrap font-mono">
            {HELP_TEXT.trim()}
          </pre>
        </div>
      )}

      {/* Editor */}
      <div className="relative">
        <textarea
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          className="w-full h-96 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm resize-none"
          placeholder="Enter your form markdown here..."
          spellCheck={false}
        />
        {parseError && (
          <div className="absolute bottom-2 left-2 right-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
            <strong>Parse Error:</strong> {parseError}
          </div>
        )}
      </div>
    </div>
  );
}
